
import React, { useState, useCallback } from 'react';
import Header from '@/components/layout/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSearchParams } from 'react-router-dom';
import { Users, Heart, MessageSquare, UserRoundPlus } from 'lucide-react';
import MusiciansMatchSystem from '@/components/musicians/MusiciansMatchSystem';
import { Card } from '@/components/ui/card';

const MusiciansNetwork: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'discover';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = useCallback((value: string) => {
    console.log(`MusiciansNetwork: Changing main tab from ${activeTab} to ${value}`);
    setActiveTab(value);
    setSearchParams({ tab: value });
  }, [activeTab, setSearchParams]);

  return (
    <div className="min-h-screen bg-background bg-gradient-to-b from-background to-blue-900/10">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMTAwJyBoZWlnaHQ9JzEwMCcgdmlld0JveD0nMCAwIDEwMCAxMDAnIHhtbG5zPSdodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zyc+PHBhdGggZD0nTTExIDE4YzMuODY2IDAgNy0zLjEzNCA3LTdzLTMuMTM0LTctNy03LTcgMy4xMzQtNyA3IDMuMTM0IDcgNyA3em00OCAyNWMzLjg2NiAwIDctMy4xMzQgNy03cy0zLjEzNC03LTctNy03IDMuMTM0LTcgNyAzLjEzNCA3IDcgN3ptLTQzLTdjMS42NTcgMCAzLTEuMzQzIDMtM3MtMS4zNDMtMy0zLTMtMyAxLjM0My0zIDMgMS4zNDMgMyAzIDN6bTYzIDMxYzEuNjU3IDAgMy0xLjM0MyAzLTNzLTEuMzQzLTMtMy0zLTMgMS4zNDMtMyAzIDEuMzQzIDMgMyAzek0zNCA5MGMxLjY1NyAwIDMtMS4zNDMgMy0zcy0xLjM0My0zLTMtMy0zIDEuMzQzLTMgMyAxLjM0MyAzIDMgM3ptNTYtNzZjMS42NTcgMCAzLTEuMzQzIDMtM3MtMS4zNDMtMy0zLTMtMyAxLjM0My0zIDMgMS4zNDMgMyAzIDN6TTEyIDg2YzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMjgtNjVjMi4yMSAwIDQtMS43OSA0LTRzLTEuNzktNC00LTQtNCAxLjc5LTQgNCAxLjc5IDQgNCA0em0yMy0xMWMyLjc2IDAgNS0yLjI0IDUtNXMtMi4yNC01LTUtNS01IDIuMjQtNSA1IDIuMjQgNSA1IDV6bS02IDYwYzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMjkgMjJjMi43NiAwIDUtMi4yNCA1LTVzLTIuMjQtNS01LTUtNSAyLjI0LTUgNSAyLjI0IDUgNSA1ek0zMiA2M2MyLjc2IDAgNS0yLjI0IDUtNXMtMi4yNC01LTUtNS01IDIuMjQtNSA1IDIuMjQgNSA1IDV6bTU3LTEzYzIuNzYgMCA1LTIuMjQgNS01cy0yLjI0LTUtNS01LTUgMi4yNC01IDUgMi4yNCA1IDUgNXptLTktMjFjMS4xMDUgMCAyLS44OTUgMi0ycy0uODk1LTItMi0yLTIgLjg5NS0yIDIgLjg5NSAyIDIgMnpNNjAgOTFjMS4xMDUgMCAyLS44OTUgMi0ycy0uODk1LTItMi0yLTIgLjg5NS0yIDIgLjg5NSAyIDIgMnpNMzUgNDFjMS4xMDUgMCAyLS44OTUgMi0ycy0uODk1LTItMi0yLTIgLjg5NS0yIDIgLjg5NSAyIDIgMnpNMTIgNjBjMS4xMDUgMCAyLS44OTUgMi0ycy0uODk1LTItMi0yLTIgLjg5NS0yIDIgLjg5NSAyIDIgMnonIGZpbGw9JyM5Yjg3ZjUnIGZpbGwtb3BhY2l0eT0nMC4wMycgZmlsbC1ydWxlPSdldmVub2RkJy8+PC9zdmc+')]"></div>
      
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-7xl mx-auto relative z-10 overflow-y-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold flex items-center gap-3 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              <Users className="h-10 w-10 text-blue-400" />
              Musicians Network
            </h1>
            <p className="text-muted-foreground text-lg">Connect, collaborate, and grow with other artists</p>
          </div>
        </div>
        
        <div className="animate-slide-up">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-500/20 blur-2xl -z-10 rounded-3xl"></div>
            <Card variant="glass" className="overflow-hidden p-6">
              <Tabs 
                defaultValue={activeTab} 
                value={activeTab}
                onValueChange={handleTabChange}
              >
                <TabsList className="w-full grid grid-cols-4 mb-6">
                  <TabsTrigger value="discover" className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    <span>Discover</span>
                  </TabsTrigger>
                  <TabsTrigger value="connections" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>My Network</span>
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span>Messages</span>
                  </TabsTrigger>
                  <TabsTrigger value="suggestions" className="flex items-center gap-2">
                    <UserRoundPlus className="h-4 w-4" />
                    <span>Suggested</span>
                  </TabsTrigger>
                </TabsList>
                
                {/* Only render the active tab to prevent issues with unmounting */}
                {activeTab === "discover" && (
                  <TabsContent value="discover" className="focus:outline-none">
                    <MusiciansMatchSystem />
                  </TabsContent>
                )}
                
                {activeTab === "connections" && (
                  <TabsContent value="connections" className="focus:outline-none">
                    <NetworkConnectionsList />
                  </TabsContent>
                )}
                
                {activeTab === "messages" && (
                  <TabsContent value="messages" className="focus:outline-none">
                    <NetworkMessages />
                  </TabsContent>
                )}
                
                {activeTab === "suggestions" && (
                  <TabsContent value="suggestions" className="focus:outline-none">
                    <NetworkSuggestions />
                  </TabsContent>
                )}
              </Tabs>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

// Simple placeholder components for the other tabs
const NetworkConnectionsList: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <Users className="h-16 w-16 mx-auto mb-4 text-blue-500/50" />
      <h3 className="text-xl font-medium mb-2">Your Network</h3>
      <p className="text-muted-foreground">Connect with artists you've collaborated with or follow.</p>
    </div>
  </div>
);

const NetworkMessages: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <MessageSquare className="h-16 w-16 mx-auto mb-4 text-purple-500/50" />
      <h3 className="text-xl font-medium mb-2">Your Messages</h3>
      <p className="text-muted-foreground">Stay in touch with your musical collaborators.</p>
    </div>
  </div>
);

const NetworkSuggestions: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <UserRoundPlus className="h-16 w-16 mx-auto mb-4 text-green-500/50" />
      <h3 className="text-xl font-medium mb-2">Suggested Connections</h3>
      <p className="text-muted-foreground">Discover artists with compatible styles and interests.</p>
    </div>
  </div>
);

export default MusiciansNetwork;
